/**
 * Знак і логотип Clientline (docs/ux/brand/README.md, розд. 2, 4). Inline SVG на токенах:
 * плитка `--cl-brand`, фігура `--cl-brand-ink`, точка `--cl-brand-dot`, словесний знак —
 * `currentColor`. Один SVG реагує і на системну тему, і на примусову `data-theme`; поза prod
 * (`<html data-env>`) плитка жовта з темною обвідкою — це робить CSS (brand.css), без JS.
 * Геометрію взято з docs/ux/brand/assets (тест assets.test.ts звіряє шляхи).
 */
import { common as t } from '../i18n/uk/common.ts';

export const MARK_PATH = 'M18.68 10.3A7 7 0 1 0 14 22.5H18.8';
export const WORD_PATH =
  'M9.27 5.92A4.6 4.6 0 1 0 9.27 12.08M13.85 0V14M18.25 4.6V14M22.05 9H31.25A4.6 4.6 0 1 0 30.07 12.08M35.05 14V4.4M35.05 9C35.05 6 36.85 4.4 39.15 4.4C41.45 4.4 42.85 5.9 42.85 8.6V14M48.05 1.4V11.2C48.05 13.1 48.95 14 50.65 14H51.45M45.85 4.6H51.25M55.45 0V14M59.85 4.6V14M64.25 14V4.4M64.25 9C64.25 6 66.05 4.4 68.35 4.4C70.65 4.4 72.05 5.9 72.05 8.6V14M75.65 9H84.85A4.6 4.6 0 1 0 83.67 12.08';
const WORD_W = 86.1;
const WORD_H = 15.7;

interface MarkProps {
  /** Сторона знака в px: 24 у шапці, 40 на вході; не менше 20 (розд. 2). */
  size: number;
}

/** Знак — декоративний: доступну назву дає посилання (шапка) або обгортка `Logo`. */
export function Mark({ size }: MarkProps) {
  return (
    <svg
      className="brand-mark"
      width={size}
      height={size}
      viewBox="0 0 32 32"
      aria-hidden="true"
      focusable="false"
    >
      <rect className="brand-mark__tile" width="32" height="32" rx="8" />
      <rect
        className="brand-mark__tile-env"
        x="0.75"
        y="0.75"
        width="30.5"
        height="30.5"
        rx="7.25"
      />
      <path className="brand-mark__ink" d={MARK_PATH} />
      <circle className="brand-mark__dot" cx="24.3" cy="22.5" r="2.4" />
    </svg>
  );
}

/** Словесний знак: лише в складі повного логотипа, висота — ⅔ знака. */
export function Wordmark({ markSize, className }: { markSize: number; className?: string }) {
  const h = Math.round(markSize * 0.667);
  const w = Math.round((h * WORD_W) / WORD_H);
  return (
    <svg
      className={className ? `brand-word ${className}` : 'brand-word'}
      width={w}
      height={h}
      viewBox={`0 0 ${WORD_W} ${WORD_H}`}
      aria-hidden="true"
      focusable="false"
    >
      <path d={WORD_PATH} transform="translate(0 0.85)" />
      <circle cx="18.25" cy="1.75" r="1.05" />
      <circle cx="59.85" cy="1.75" r="1.05" />
    </svg>
  );
}

/** Повний логотип (знак + словесний знак) з доступною назвою «Clientline». */
export function Logo({ markSize }: { markSize: 24 | 40 }) {
  return (
    <span className={`brand-lockup brand-lockup--${markSize}`} role="img" aria-label={t.appName}>
      <Mark size={markSize} />
      <Wordmark markSize={markSize} />
    </span>
  );
}
